import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../../config/api";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../../../../../components/Sidebar";
import LanguageSwitcher from "../../../../../../components/LanguageSwitcher";
import { ProgressSteps } from "./ProgressSteps";
import { ListType } from "./attributeType/listType";
import { StringType } from "./attributeType/stringType";
import { TableType } from "./attributeType/tableType";
import PdfCropModal from "../../../../../../components/PdfCropModal";

// ── Image Gallery ─────────────────────────────────────────────────────────────
function ImageGallery({ images, language, onDelete, onAdd, fileUrl }) {
    const [selected, setSelected] = React.useState(null);
    const [showCrop, setShowCrop] = React.useState(false);
    const fileInputRef = React.useRef(null);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (ev) => onAdd(ev.target.result);
            reader.readAsDataURL(file);
        });
        e.target.value = "";
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                    {language === "zh" ? "產品圖片" : "Product Images"}
                    {images && images.length > 0 && (
                        <span className="ml-2 text-xs text-gray-400 font-normal">
                            ({images.length})
                        </span>
                    )}
                </label>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-500/10 hover:bg-orange-500/20 px-3 py-1.5 rounded-lg transition-colors"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {language === "zh" ? "新增圖片" : "Add Image"}
                </button>
                {fileUrl && (
                    <button
                        type="button"
                        onClick={() => setShowCrop(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-500/10 hover:bg-blue-500/20 px-3 py-1.5 rounded-lg transition-colors"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2m8-16h2a2 2 0 012 2v2m0 8v2a2 2 0 01-2 2h-2" />
                        </svg>
                        {language === "zh" ? "從 PDF 截取" : "Capture from PDF"}
                    </button>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
            </div>

            {images && images.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                    {images.map((src, i) => (
                        <div key={i} className="relative group">
                            <button
                                type="button"
                                onClick={() => setSelected(src)}
                                className="w-20 h-20 rounded-xl border-2 border-gray-200 overflow-hidden hover:border-orange-400 transition-colors block"
                            >
                                <img src={src} alt={`img-${i}`} className="w-full h-full object-cover" />
                            </button>
                            <button
                                type="button"
                                onClick={() => onDelete(i)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full items-center justify-center hidden group-hover:flex transition-colors shadow-sm"
                                title={language === "zh" ? "刪除圖片" : "Delete image"}
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-gray-400 text-center py-4 border-2 border-dashed border-gray-200 rounded-xl">
                    {language === "zh" ? "尚無圖片" : "No images yet"}
                </p>
            )}

            {selected && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
                    <img src={selected} alt="preview" className="max-w-3xl max-h-[85vh] rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
                </div>
            )}

            {showCrop && (
                <PdfCropModal
                    fileUrl={fileUrl}
                    language={language}
                    onCapture={(dataUrl) => { onAdd(dataUrl); setShowCrop(false); }}
                    onClose={() => setShowCrop(false)}
                />
            )}
        </div>
    );
}

/** Append the freshly added (data-URL) images to *formData* as file blobs. */
function appendNewImages(formData, images) {
    images.forEach((src, idx) => {
        if (typeof src === "string" && src.startsWith("data:")) {
            const [header, b64] = src.split(",");
            const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
            const ext = mime.split("/")[1] || "jpeg";
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            formData.append("images", new Blob([bytes], { type: mime }), `img_${idx}.${ext}`);
        }
    });
}

function ProductProperties() {
    const { currentUser, getUserProfile } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const profile = getUserProfile();

    // ── Edit mode ─────────────────────────────────────────────────────────────
    // Entered from the product detail modal as
    //   /product-properties?mode=edit&product_id=<id>&version_id=<id>
    // The IDs live in the URL, not only in navigation state, so a reload
    // re-fetches the version instead of bouncing back to /product.
    // location.state is a prefill fast-path that disappears on refresh.
    const searchParams = new URLSearchParams(location.search);
    const isEditMode = searchParams.get("mode") === "edit";
    const editProductId =
        location.state?.productId ??
        (Number(searchParams.get("product_id")) || null);
    const editVersionId =
        location.state?.versionId ??
        (Number(searchParams.get("version_id")) || null);
    const [editVersionLabel, setEditVersionLabel] = useState(
        location.state?.versionLabel || "",
    );
    // Reload path only — true until the form has been rebuilt from the API.
    const [hydrating, setHydrating] = useState(
        isEditMode && !location.state?.mode,
    );

    // PDF viewer states
    const [pdfError] = useState(null);

    // Get product data from navigation state
    const file = location.state?.file || null;
    const [fileUrl, setFileUrl] = useState(null);
    // Edit mode has no local File — the datasheet is previewed from its GCS URL.
    const [editPdfUrl, setEditPdfUrl] = useState(location.state?.pdfUrl || null);
    const [productName, setProductName] = useState(
        location.state?.productName || "",
    );
    const [productCategory, setProductCategory] = useState(
        location.state?.productCategory || {},
    );
    const [selectedProducerId, setSelectedProducerId] = useState(
        location.state?.producerId ?? null,
    );
    // product compliance — backend now sends proper booleans
    const [rohsCompliance, setRohsCompliance] = useState(
        location.state?.rohsCompliant ?? false,
    );
    const [reachCompliance, setReachCompliance] = useState(
        location.state?.reachCompliant ?? false,
    );
    const [ulComparable, setUlComparable] = useState(
        location.state?.ulComparable ?? false,
    );
    const [productDescription, setProductDescription] = useState(
        location.state?.productDescription || "",
    );
    // attributes[] from Document AI — used both as the redirect guard and
    // the initial state for the attribute editor
    const [productData] = useState(() => location.state?.attributes || []);

    const [loading, setLoading] = useState(false);

    // Validation errors — keys match field names, values are error messages
    const [validationErrors, setValidationErrors] = useState({});

    // Create file URL for PDF viewer
    useEffect(() => {
        if (file) {
            const url = URL.createObjectURL(file);
            setFileUrl(url);

            // Cleanup function to revoke the URL
            return () => URL.revokeObjectURL(url);
        }
    }, [file]);

    const getLangFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return params.get("lang") === "zh" ? "zh" : "en";
    }, [location.search]);

    const [language, setLanguage] = useState(getLangFromUrl);

    // Keep language in sync with URL
    useEffect(() => {
        setLanguage(getLangFromUrl());
    }, [getLangFromUrl]);

    // Update URL when language changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("lang") !== language) {
            params.set("lang", language);
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [language, navigate, location.search]);

    // Redirect if not authenticated
    useEffect(() => {
        if (!currentUser) {
            navigate({ pathname: "/login", search: `?lang=${language}` });
        }
    }, [currentUser, navigate, language]);

    // Redirect if no product data or file (only after we've checked).
    // Edit mode is exempt: the attributes arrive from the API on the reload
    // path, and an edited product is allowed to have none at all.
    useEffect(() => {
        if (isEditMode) return;
        if (!productData || productData.length === 0) {
            console.warn("No product data found, redirecting to /update");
            navigate({ pathname: "/product", search: `?lang=${language}` });
        }
    }, [productData, navigate, language, isEditMode]);

    // Edit mode has no local File — preview the stored datasheet from GCS.
    useEffect(() => {
        if (isEditMode && editPdfUrl) setFileUrl(editPdfUrl);
    }, [isEditMode, editPdfUrl]);

    // Reload path: navigation state is gone, so rebuild the form from the API.
    useEffect(() => {
        if (!isEditMode || !hydrating || !currentUser) return;
        if (!editProductId || !editVersionId) {
            navigate({ pathname: "/product", search: `?lang=${language}` });
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const token = await currentUser.getIdToken();
                const headers = { Authorization: `Bearer ${token}` };
                const [productRes, versionsRes] = await Promise.all([
                    axios.get(`${getApiUrl()}/api/v2/products/${editProductId}`, {
                        headers,
                    }),
                    axios.get(
                        `${getApiUrl()}/api/v2/products/${editProductId}/versions`,
                        { headers },
                    ),
                ]);
                if (cancelled) return;

                const product = productRes.data?.data;
                const version = (versionsRes.data?.data || []).find(
                    (v) => String(v.version_id) === String(editVersionId),
                );
                if (!product || !version) throw new Error("Version not found");

                // Same merge the detail modal shows: the snapshot wins over the
                // product row, so a non-English version keeps its own values.
                const data = { ...product, ...(version.snapshot_data || {}) };
                setProductName(data.model_id || "");
                setProductDescription(data.description || "");
                setProductAttributes(data.attributes || []);
                setProductImages(data.images || []);
                setEditPdfUrl(data.pdf_url || null);
                setRohsCompliance(!!data.rohs_compliant);
                setReachCompliance(!!data.reach_compliant);
                setUlComparable(!!data.ul_comparable);
                setSelectedProducerId(data.producer_id ?? null);
                setEditVersionLabel(version.version_number || "");
                setSelectedLangCode(
                    (version.version_number || "en-1").split("-")[0],
                );
                setProductCategory({
                    categoryId: product.category_id,
                    categoryName: product.category_name || "",
                    subCategoryName: product.subcategory_name || "",
                    subSubCategoryName: product.subsubcategory_name || "",
                });
            } catch (err) {
                console.error("Failed to load version for editing:", err);
                alert(
                    language === "zh"
                        ? "無法載入此版本"
                        : "Failed to load this version",
                );
                navigate(
                    { pathname: "/product", search: `?lang=${language}` },
                    { state: { activeTab: "database" } },
                );
            } finally {
                if (!cancelled) setHydrating(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        isEditMode,
        hydrating,
        currentUser,
        editProductId,
        editVersionId,
        language,
        navigate,
    ]);

    // fetch available producer from database to show in the dropdown
    const [availableProducers, setAvailableProducers] = useState([]);

    const fetchProducers = useCallback(async () => {
        const token = await currentUser.getIdToken();

        const response = await axios.get(`${getApiUrl()}/api/v2/producers`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (response.status === 200) {
            const producers = response.data.data || [];
            setAvailableProducers(producers); // full objects: { producer_id, producer_string, role }
        } else {
            console.error("Failed to fetch producers:", response.status);
        }
    }, [currentUser]);

    useEffect(() => {
        if (currentUser) {
            fetchProducers();
        }
    }, [currentUser, fetchProducers]);

    // ── Language codes for version naming ─────────────────────────────────────
    const [availableLangCodes, setAvailableLangCodes] = useState([]);
    // An edit is fixed to the language of the version it started from. The
    // upload flow deliberately leaves this blank until the code has been
    // confirmed against the API list, so only edit mode pre-seeds it.
    const [selectedLangCode, setSelectedLangCode] = useState(
        isEditMode ? location.state?.selectedLangCode || "" : "",
    );

    useEffect(() => {
        const fetchLangCodes = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                const res = await axios.get(
                    `${getApiUrl()}/api/v2/products/lang-codes`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    },
                );
                if (res.data?.data) {
                    setAvailableLangCodes(res.data.data);
                    // Use the lang code carried over from UploadPdf if available,
                    // otherwise default to the first option from the API.
                    const passedLangCode = location.state?.selectedLangCode;
                    if (
                        passedLangCode &&
                        res.data.data.some((c) => c.id === passedLangCode)
                    ) {
                        setSelectedLangCode(passedLangCode);
                    }
                    // No fallback — leave blank so the user must choose manually
                }
            } catch (err) {
                console.warn("Failed to fetch lang codes:", err);
            }
        };
        fetchLangCodes();
    }, [currentUser, location.state?.selectedLangCode]);

    const [productImages, setProductImages] = useState(
        () => location.state?.images || [],
    );

    // Seed the attribute editor directly from the normalised Document AI output.
    // Shape from backend: [{ dataType: "list"|"table"|"string", attributeName, data }]
    // This is already what ListType / StringType / TableType expect.
    const [productAttributes, setProductAttributes] = useState(
        () => location.state?.attributes || [],
    );

    const [attributeTypePicker, setAttributeTypePicker] = useState(false);

    // Duplicate-product modal state
    // When check-and-create returns status="exists", we stash the existing
    // product info + the FormData so the user can choose an action.
    const [duplicateModal, setDuplicateModal] = useState({
        open: false,
        existingProduct: null, // { product_id, model_id, ... }
        formData: null, // the FormData ready to re-send
        token: null, // auth token so we don't re-fetch
        versionCount: 0, // number of existing versions
    });

    const handleAddAttribute = (type) => {
        const defaultData = {
            string: "",
            list: [],
            table: [], // flat array of row-objects: [{ colName: value, ... }]
        };

        const newAttribute = {
            dataType: type,
            attributeName: "",
            data: defaultData[type] ?? {},
            ...(type === "table" ? { notes: [] } : {}),
        };
        setProductAttributes((prev) => [newAttribute, ...prev]);
        setAttributeTypePicker(false);
    };

    const handleUpdateAttribute = (index, updatedAttribute) => {
        setProductAttributes((prev) =>
            prev.map((attr, i) => (i === index ? updatedAttribute : attr)),
        );
    };

    const handleDeleteAttribute = (index) => {
        setProductAttributes((prev) => prev.filter((_, i) => i !== index));
    };
    const handleMoveAttribute = (index, dir) => {
        setProductAttributes((prev) => {
            const attrs = [...prev];
            const target = index + dir;
            if (target < 0 || target >= attrs.length) return prev;
            [attrs[index], attrs[target]] = [attrs[target], attrs[index]];
            return attrs;
        });
    };

    const handleSaveData = async () => {
        try {
            setLoading(true);

            // Collect all validation errors at once
            const errors = {};
            if (!file)
                errors.file =
                    language === "zh"
                        ? "未選擇PDF文件"
                        : "No PDF file selected";
            if (!productName.trim())
                errors.productName =
                    language === "zh"
                        ? "產品名稱不能為空"
                        : "Product name cannot be empty";
            if (!productCategory.categoryName?.trim())
                errors.productCategory =
                    language === "zh"
                        ? "產品類別不能為空"
                        : "Product category cannot be empty";
            if (!location.state?.productCategory?.categoryId)
                errors.productCategory =
                    language === "zh"
                        ? "未選擇產品類別"
                        : "Product category is not selected";
            if (!selectedProducerId)
                errors.producerId =
                    language === "zh"
                        ? "未選擇生產商"
                        : "Product producer is not selected";
            if (!selectedLangCode)
                errors.langCode =
                    language === "zh"
                        ? "未選擇產品語言"
                        : "Product language is not selected";

            if (Object.keys(errors).length > 0) {
                setValidationErrors(errors);
                // Build a summary alert
                const messages = Object.values(errors).join("\n• ");
                alert(
                    language === "zh"
                        ? `請修正以下錯誤：\n• ${messages}`
                        : `Please fix the following errors:\n• ${messages}`,
                );
                return;
            }

            setValidationErrors({});

            const token = await currentUser.getIdToken();

            // Normalize strings: trim and lowercase
            const normalizedProductName = productName.trim().toLowerCase();

            // Use the deepest selected level's ID (sub-sub → sub → category).
            const resolvedCategoryId =
                location.state?.productCategory?.subSubCategoryId ??
                location.state?.productCategory?.subCategoryId ??
                location.state?.productCategory?.categoryId;

            // Create FormData for file upload
            const formData = new FormData();
            formData.append("pdf", file);
            formData.append("model_id", normalizedProductName);
            formData.append("category_id", resolvedCategoryId);
            formData.append("producer_id", selectedProducerId);
            formData.append("description", productDescription.trim());
            formData.append("attributes", JSON.stringify(productAttributes));
            formData.append("rohs_compliant", rohsCompliance);
            formData.append("reach_compliant", reachCompliance);
            formData.append("ul_comparable", ulComparable);
            formData.append("lang_code", selectedLangCode);

            appendNewImages(formData, productImages);

            const res = await axios.post(
                `${getApiUrl()}/api/v2/products/check-and-create`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            console.log("PDF upload response:", res.data);

            // Backend returns 200 + status="exists" when a duplicate is found
            if (res.data?.data?.status === "exists") {
                // If the user already confirmed "New Version" in the Upload page,
                // skip the modal and go straight to the update flow.
                if (location.state?.duplicateChecked) {
                    const existingId =
                        location.state?.existingProduct?.product_id ??
                        res.data.data.product?.product_id;
                    if (existingId) {
                        await _doUpdate(
                            existingId,
                            token,
                            location.state?.versionLabel,
                        );
                        return;
                    }
                }

                // Fetch per-language version count is no longer needed —
                // the backend auto-generates the version label.

                // Fallback — show modal so the user can decide
                setDuplicateModal({
                    open: true,
                    existingProduct: res.data.data.product,
                    formData,
                    token,
                    versionCount: 0,
                    versionSummary: res.data.data.version_summary || {},
                });
                return; // don't navigate — let the modal handle it
            }

            // status="created" — success
            alert(
                language === "zh" ? "數據已保存！" : "Data saved successfully!",
            );
            navigate({ pathname: "/product", search: `?lang=${language}` });
        } catch (error) {
            console.error("Error uploading PDF:", error);
            const errorMessage =
                error.response?.data?.error || error.message || "Unknown error";
            alert(
                language === "zh"
                    ? `上傳失敗：${errorMessage}`
                    : `Upload failed: ${errorMessage}`,
            );
        } finally {
            setLoading(false);
        }
    };

    /**
     * Edit mode — save the edited form as a NEW version of the same product.
     *
     * model_id and category_id are deliberately not sent: the name and category
     * are locked while editing, and omitting a field leaves the stored value
     * untouched. source_version_id tells the backend which snapshot the edit
     * started from, so anything not sent (the datasheet in particular) is
     * carried over from that version rather than from the English product row.
     */
    const handleUpdateVersion = async () => {
        try {
            setLoading(true);

            if (!selectedProducerId) {
                const message =
                    language === "zh"
                        ? "未選擇生產商"
                        : "Product producer is not selected";
                setValidationErrors({ producerId: message });
                alert(message);
                return;
            }
            setValidationErrors({});

            const token = await currentUser.getIdToken();
            const langCode =
                selectedLangCode || editVersionLabel.split("-")[0] || "en";

            const formData = new FormData();
            formData.append("producer_id", selectedProducerId);
            formData.append("description", productDescription.trim());
            formData.append("attributes", JSON.stringify(productAttributes));
            formData.append("rohs_compliant", rohsCompliance);
            formData.append("reach_compliant", reachCompliance);
            formData.append("ul_comparable", ulComparable);
            formData.append("lang_code", langCode);
            formData.append("source_version_id", editVersionId);

            // Existing images the user kept, in display order — the backend
            // copies these into the new version's folder and appends the newly
            // added ones after them, so a removal sticks and an addition never
            // displaces what was already there.
            formData.append(
                "retained_images",
                JSON.stringify(
                    productImages.filter(
                        (src) =>
                            typeof src === "string" && !src.startsWith("data:"),
                    ),
                ),
            );
            appendNewImages(formData, productImages);

            const res = await axios.put(
                `${getApiUrl()}/api/v2/products/${editProductId}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            const newLabel = res.data?.data?.version || "";
            alert(
                language === "zh"
                    ? `已儲存為新版本 ${newLabel}`
                    : `Saved as new version ${newLabel}`,
            );
            navigate(
                { pathname: "/product", search: `?lang=${language}` },
                { state: { activeTab: "database" } },
            );
        } catch (error) {
            console.error("Error updating version:", error);
            const errorMessage =
                error.response?.data?.error || error.message || "Unknown error";
            alert(
                language === "zh"
                    ? `更新失敗：${errorMessage}`
                    : `Update failed: ${errorMessage}`,
            );
        } finally {
            setLoading(false);
        }
    };

    // ── Duplicate modal actions ──────────────────────────────────────────────

    /**
     * Shared helper – PUT the current form data as a new version of an
     * existing product.  Used by both the auto-flow (duplicateChecked) and
     * the manual modal flow.
     */
    const _doUpdate = async (existingId, token, versionLabel = null) => {
        if (!existingId) throw new Error("Missing existing product ID");

        const formData = new FormData();
        formData.append("pdf", file);
        formData.append("model_id", productName.trim().toLowerCase());
        formData.append(
            "category_id",
            location.state?.productCategory?.subSubCategoryId ??
                location.state?.productCategory?.subCategoryId ??
                location.state?.productCategory?.categoryId,
        );
        formData.append("producer_id", selectedProducerId);
        formData.append("description", productDescription.trim());
        formData.append("attributes", JSON.stringify(productAttributes));
        formData.append("rohs_compliant", rohsCompliance);
        formData.append("reach_compliant", reachCompliance);
        formData.append("ul_comparable", ulComparable);

        appendNewImages(formData, productImages);

        // version_label: from Upload page flow, or from fallback modal selection
        const label = versionLabel || location.state?.versionLabel || null;
        if (label) {
            formData.append("version_label", label);
        }
        // Always send lang_code so backend can auto-generate if no label
        formData.append("lang_code", selectedLangCode);

        await axios.put(
            `${getApiUrl()}/api/v2/products/${existingId}`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        alert(
            language === "zh"
                ? "現有產品已更新為新版本！"
                : "Existing product updated with a new version!",
        );
        navigate({ pathname: "/product", search: `?lang=${language}` });
    };

    const handleUpdateExisting = async () => {
        try {
            setLoading(true);
            const existingId = duplicateModal.existingProduct?.product_id;
            // Let the backend auto-generate the version label based on lang_code
            const versionLabel = null;
            setDuplicateModal((prev) => ({ ...prev, open: false }));

            const token = await currentUser.getIdToken();
            await _doUpdate(existingId, token, versionLabel);
        } catch (error) {
            console.error("Update existing error:", error);
            const msg =
                error.response?.data?.error || error.message || "Unknown error";
            alert(
                language === "zh"
                    ? `更新失敗：${msg}`
                    : `Update failed: ${msg}`,
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCancelDuplicate = () => {
        setDuplicateModal({
            open: false,
            existingProduct: null,
            formData: null,
            token: null,
            versionCount: 0,
        });
    };

    const handleGoBack = () => {
        navigate(
            { pathname: "/product", search: `?lang=${language}` },
            // Editing starts from the database tab, uploading from the upload tab.
            { state: { activeTab: isEditMode ? "database" : "upload" } },
        );
    };

    if (!currentUser || !profile || hydrating) {
        return (
            <div className="flex h-screen bg-dashboard-bg text-black font-sans">
                <LanguageSwitcher
                    language={language}
                    setLanguage={setLanguage}
                />
                <div className="flex-1 flex flex-col gap-8 items-center justify-center -mt-36">
                    <div className="flex flex-col gap-4 items-center text-center">
                        <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                                <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"></div>
                                <div
                                    className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "0.1s" }}
                                ></div>
                                <div
                                    className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "0.2s" }}
                                ></div>
                            </div>
                            <span className="text-orange-500 text-lg font-semibold">
                                {language === "zh"
                                    ? "正在加載數據..."
                                    : "Loading data..."}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-dashboard-bg text-black font-sans">
            <Sidebar language={language} />
            <LanguageSwitcher language={language} setLanguage={setLanguage} />

            {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 z-10 rounded-lg">
                    <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}

            <div className="w-5/6 flex-1 flex flex-col gap-2 p-8 md:p-16 pt-24 md:pt-24 overflow-y-auto">
                {/* Progress Tracker — the upload wizard's steps say nothing
                    about an edit, so it is replaced by the version banner. */}
                {isEditMode ? (
                    <div className="flex items-center justify-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                        <svg
                            className="w-4 h-4 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                            />
                        </svg>
                        {language === "zh"
                            ? `正在編輯版本 ${editVersionLabel} — 儲存後會建立新版本，原版本保持不變。`
                            : `Editing version ${editVersionLabel} — saving creates a new version; ${editVersionLabel} stays as it is.`}
                    </div>
                ) : (
                    <ProgressSteps currentStep={2} language={language} />
                )}

                <div className="flex flex-row justify-center items-center">
                    <button
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                        onClick={handleGoBack}
                    >
                        ← {language === "zh" ? "返回" : "Back"}
                    </button>
                    <div className="mt-8 mb-4 flex-1 justify-self-center flex-col items-center text-center">
                        <h1 className="text-3xl font-bold text-white mb-2">
                            {productName}
                        </h1>
                        <p className="text-slate-100">
                            {language === "zh"
                                ? "編輯和管理產品屬性信息"
                                : "Edit and manage product property information"}
                        </p>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-6">
                    {fileUrl && (
                        <div className="lg:w-2/5 mb-8 lg:mb-0">
                            <div className="bg-white rounded-2xl border border-gray-200 p-2 sticky top-4">
                                {/* PDF Viewer */}
                                {pdfError ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                                        <svg
                                            className="w-16 h-16 mx-auto text-red-400 mb-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                            />
                                        </svg>
                                        <p className="text-red-600 font-medium mb-2">
                                            {language === "zh"
                                                ? "PDF載入失敗"
                                                : "Failed to load PDF"}
                                        </p>
                                        <p className="text-gray-600 text-sm mb-4">
                                            {pdfError.message ||
                                                "Unknown error"}
                                        </p>
                                        <a
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-block px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                                        >
                                            {language === "zh"
                                                ? "在新視窗開啟"
                                                : "Open in New Window"}
                                        </a>
                                    </div>
                                ) : (
                                    <div
                                        className="bg-gray-100 rounded-lg overflow-hidden"
                                        style={{
                                            height: "700px",
                                            width: "100%",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {fileUrl ? (
                                            // <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                                            //     <Viewer
                                            //         fileUrl={fileUrl}
                                            //         plugins={[
                                            //             defaultLayoutPluginInstance,
                                            //         ]}
                                            //         onDocumentLoadError={(
                                            //             error
                                            //         ) => {
                                            //             console.error(
                                            //                 "Error loading PDF:",
                                            //                 error
                                            //             );
                                            //             setPdfError(error);
                                            //         }}
                                            //     />
                                            // </Worker>
                                            // <div className="h-full">
                                            // <PdfViewer fileUrl={fileUrl} />
                                            <iframe
                                                src={fileUrl}
                                                title="PDF Preview"
                                                className="w-full h-full border-0"
                                            ></iframe>
                                        ) : (
                                            // </div>
                                            <div className="flex items-center justify-center h-full">
                                                <p className="text-gray-500">
                                                    {language === "zh"
                                                        ? "無PDF可預覽"
                                                        : "No PDF to preview"}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div
                        className={`bg-white rounded-2xl border border-gray-200 p-6 md:p-8 ${
                            fileUrl ? "lg:w-3/5" : "w-full"
                        }`}
                    >
                        <div className="py-4 flex flex-col gap-8">
                            <div className="flex md:flex-row flex-col gap-4">
                                <div className="md:w-1/2 w-full flex flex-col gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {language === "zh"
                                                ? "產品名稱"
                                                : "Product Name"}
                                        </label>
                                        <input
                                            type="text"
                                            value={productName}
                                            // The name identifies the product across
                                            // every version — renaming belongs to the
                                            // product, not to editing one version.
                                            disabled={isEditMode}
                                            className={`w-full border rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 ${validationErrors.productName ? "border-red-500 ring-2 ring-red-200" : "border-gray-300"}`}
                                            placeholder={
                                                language === "zh"
                                                    ? "輸入產品名稱"
                                                    : "Enter product name"
                                            }
                                            onChange={(e) => {
                                                setProductName(e.target.value);
                                                if (
                                                    validationErrors.productName
                                                )
                                                    setValidationErrors(
                                                        (prev) => {
                                                            const {
                                                                productName,
                                                                ...rest
                                                            } = prev;
                                                            return rest;
                                                        },
                                                    );
                                            }}
                                            required
                                        />
                                        {validationErrors.productName && (
                                            <p className="text-red-500 text-xs mt-1">
                                                {validationErrors.productName}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="md:w-1/2 w-full flex flex-col gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {language === "zh"
                                                ? "產品類別"
                                                : "Product Category"}
                                        </label>
                                        <input
                                            type="text"
                                            value={productCategory.categoryName}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {language === "zh"
                                                ? "產品子類別"
                                                : "Product Sub-category"}
                                        </label>
                                        <input
                                            type="text"
                                            value={
                                                productCategory.subCategoryName
                                            }
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                            placeholder={
                                                language === "zh"
                                                    ? "無選產品子類別"
                                                    : "No Product Sub-category Selected"
                                            }
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {language === "zh"
                                                ? "產品細分類別"
                                                : "Product Sub-sub-category"}
                                        </label>
                                        <input
                                            type="text"
                                            value={
                                                productCategory.subSubCategoryName ||
                                                ""
                                            }
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                            placeholder={
                                                language === "zh"
                                                    ? "無選產品細分類別"
                                                    : "No Product Sub-sub-category Selected"
                                            }
                                            disabled
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="w-full md:w-1/2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {language === "zh"
                                            ? "產品描述"
                                            : "Product Description"}
                                    </label>
                                    <textarea
                                        value={productDescription}
                                        rows={2}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                        placeholder={
                                            language === "zh"
                                                ? "輸入產品描述"
                                                : "Enter product description"
                                        }
                                        onChange={(e) =>
                                            setProductDescription(
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                                <div className="w-full md:w-1/2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {language === "zh"
                                            ? "生產商名稱"
                                            : "Producer Name"}
                                    </label>
                                    <select
                                        value={selectedProducerId ?? ""}
                                        onChange={(e) => {
                                            const id = e.target.value
                                                ? Number(e.target.value)
                                                : null;
                                            setSelectedProducerId(id);
                                            if (validationErrors.producerId)
                                                setValidationErrors((prev) => {
                                                    const {
                                                        producerId,
                                                        ...rest
                                                    } = prev;
                                                    return rest;
                                                });
                                        }}
                                        className={`w-full border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 ${validationErrors.producerId ? "border-red-500 ring-2 ring-red-200" : "border-gray-300"}`}
                                    >
                                        <option value="">
                                            {language === "zh"
                                                ? "選擇生產商"
                                                : "Select a producer"}
                                        </option>
                                        {availableProducers.map((p) => (
                                            <option
                                                key={p.producer_id}
                                                value={p.producer_id}
                                            >
                                                {p.producer_string}
                                                {p.role ? ` (${p.role})` : ""}
                                            </option>
                                        ))}
                                    </select>
                                    {validationErrors.producerId && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {validationErrors.producerId}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-row">
                                    <label className="text-sm font-medium text-gray-700 self-end">
                                        {language === "zh"
                                            ? "產品合規性"
                                            : "Product Compliance"}
                                    </label>
                                </div>
                                <div className="max-h-48 overflow-y-auto px-5">
                                    <div className="flex flex-col gap-1">
                                        <div
                                            className="flex justify-between items-center p-2 bg-white border border-gray-300 hover:bg-gray-300 rounded-lg cursor-pointer"
                                            onClick={() =>
                                                setReachCompliance(
                                                    !reachCompliance,
                                                )
                                            }
                                        >
                                            <label className="text-sm font-medium text-gray-700 cursor-pointer">
                                                REACH Compliant
                                            </label>
                                            <input
                                                type="checkbox"
                                                checked={reachCompliance}
                                                onChange={() => {}}
                                                className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer pointer-events-none"
                                            />
                                        </div>
                                        <div
                                            className="flex justify-between items-center p-2 bg-white border border-gray-300 hover:bg-gray-300 rounded-lg cursor-pointer"
                                            onClick={() =>
                                                setRohsCompliance(
                                                    !rohsCompliance,
                                                )
                                            }
                                        >
                                            <label className="text-sm font-medium text-gray-700 cursor-pointer">
                                                RoHS Compliant
                                            </label>
                                            <input
                                                type="checkbox"
                                                checked={rohsCompliance}
                                                onChange={() => {}}
                                                className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer pointer-events-none"
                                            />
                                        </div>
                                        <div
                                            className="flex justify-between items-center p-2 bg-white border border-gray-300 hover:bg-gray-300 rounded-lg cursor-pointer"
                                            onClick={() =>
                                                setUlComparable(!ulComparable)
                                            }
                                        >
                                            <label className="text-sm font-medium text-gray-700 cursor-pointer">
                                                UL Comparable
                                            </label>
                                            <input
                                                type="checkbox"
                                                checked={ulComparable}
                                                onChange={() => {}}
                                                className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer pointer-events-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Images */}
                            <ImageGallery
                                images={productImages}
                                language={language}
                                fileUrl={fileUrl}
                                onDelete={(i) =>
                                    setProductImages((prev) =>
                                        prev.filter((_, idx) => idx !== i),
                                    )
                                }
                                onAdd={(dataUrl) =>
                                    setProductImages((prev) => [
                                        ...prev,
                                        dataUrl,
                                    ])
                                }
                            />

                            <div className="flex flex-col gap-4 mb-2 mt-5">
                                <div className="flex items-center justify-between">
                                    <label className="block text-xl font-bold text-gray-700">
                                        {language === "zh"
                                            ? "產品屬性"
                                            : "Product Attributes"}
                                    </label>
                                    <div className="relative">
                                        <button
                                            type="button"
                                            className="bg-green-500 outline outline-1 outline-green-200 px-4 py-1 rounded-lg text-white hover:bg-green-600 transition-colors text-xs md:text-sm"
                                            onClick={() =>
                                                setAttributeTypePicker(
                                                    !attributeTypePicker,
                                                )
                                            }
                                        >
                                            +{" "}
                                            {language === "zh"
                                                ? "添加產品屬性"
                                                : "Add Product Attribute"}
                                        </button>
                                        {attributeTypePicker && (
                                            <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
                                                <button
                                                    type="button"
                                                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors"
                                                    onClick={() =>
                                                        handleAddAttribute(
                                                            "string",
                                                        )
                                                    }
                                                >
                                                    {language === "zh"
                                                        ? "字串類型"
                                                        : "String Type"}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-purple-50 transition-colors"
                                                    onClick={() =>
                                                        handleAddAttribute(
                                                            "list",
                                                        )
                                                    }
                                                >
                                                    {language === "zh"
                                                        ? "列表類型"
                                                        : "List Type"}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-purple-50 transition-colors"
                                                    onClick={() =>
                                                        handleAddAttribute(
                                                            "table",
                                                        )
                                                    }
                                                >
                                                    {language === "zh"
                                                        ? "表格類型"
                                                        : "Table Type"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <hr className="border-gray-300" />

                                {productAttributes.length === 0 ? (
                                    <p className="text-gray-400 text-sm text-center py-3">
                                        {language === "zh"
                                            ? "尚未添加任何屬性"
                                            : "No attributes added yet"}
                                    </p>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {productAttributes.map(
                                            (attr, index) => {
                                                const total = productAttributes.length;
                                                const moveButtons = (
                                                    <div className="flex flex-col gap-0.5 ml-1 shrink-0">
                                                        <button
                                                            type="button"
                                                            disabled={index === 0}
                                                            onClick={() => handleMoveAttribute(index, -1)}
                                                            className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                            title={language === "zh" ? "上移" : "Move up"}
                                                        >
                                                            ▲
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={index === total - 1}
                                                            onClick={() => handleMoveAttribute(index, 1)}
                                                            className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                            title={language === "zh" ? "下移" : "Move down"}
                                                        >
                                                            ▼
                                                        </button>
                                                    </div>
                                                );
                                                if (attr.dataType === "list") {
                                                    return (
                                                        <div key={index} className="flex items-start">
                                                            <div className="flex-1 min-w-0">
                                                                <ListType
                                                                    attribute={attr}
                                                                    onChange={(
                                                                        updated,
                                                                    ) =>
                                                                        handleUpdateAttribute(
                                                                            index,
                                                                            updated,
                                                                        )
                                                                    }
                                                                    onDelete={() =>
                                                                        handleDeleteAttribute(
                                                                            index,
                                                                        )
                                                                    }
                                                                    language={language}
                                                                />
                                                            </div>
                                                            {moveButtons}
                                                        </div>
                                                    );
                                                }

                                                if (
                                                    attr.dataType === "string"
                                                ) {
                                                    return (
                                                        <div key={index} className="flex items-start">
                                                            <div className="flex-1 min-w-0">
                                                                <StringType
                                                                    attribute={attr}
                                                                    onChange={(
                                                                        updated,
                                                                    ) =>
                                                                        handleUpdateAttribute(
                                                                            index,
                                                                            updated,
                                                                        )
                                                                    }
                                                                    onDelete={() =>
                                                                        handleDeleteAttribute(
                                                                            index,
                                                                        )
                                                                    }
                                                                    language={language}
                                                                />
                                                            </div>
                                                            {moveButtons}
                                                        </div>
                                                    );
                                                }

                                                if (attr.dataType === "table") {
                                                    return (
                                                        <div key={index} className="flex items-start">
                                                            <div className="flex-1 min-w-0">
                                                                <TableType
                                                                    attribute={attr}
                                                                    onChange={(
                                                                        updated,
                                                                    ) =>
                                                                        handleUpdateAttribute(
                                                                            index,
                                                                            updated,
                                                                        )
                                                                    }
                                                                    onDelete={() =>
                                                                        handleDeleteAttribute(
                                                                            index,
                                                                        )
                                                                    }
                                                                    language={language}
                                                                />
                                                            </div>
                                                            {moveButtons}
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div
                                                        key={index}
                                                        className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex gap-2 items-center"
                                                    >
                                                        <input
                                                            type="text"
                                                            value={
                                                                attr.attributeName
                                                            }
                                                            placeholder={
                                                                language ===
                                                                "zh"
                                                                    ? "屬性名稱"
                                                                    : "Attribute name"
                                                            }
                                                            className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            onChange={(e) =>
                                                                handleUpdateAttribute(
                                                                    index,
                                                                    {
                                                                        ...attr,
                                                                        attributeName:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    },
                                                                )
                                                            }
                                                        />
                                                        <input
                                                            type="text"
                                                            value={attr.data}
                                                            placeholder={
                                                                language ===
                                                                "zh"
                                                                    ? "值"
                                                                    : "Value"
                                                            }
                                                            className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            onChange={(e) =>
                                                                handleUpdateAttribute(
                                                                    index,
                                                                    {
                                                                        ...attr,
                                                                        data: e
                                                                            .target
                                                                            .value,
                                                                    },
                                                                )
                                                            }
                                                        />
                                                        <button
                                                            type="button"
                                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                                            onClick={() =>
                                                                handleDeleteAttribute(
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>
                                                        {moveButtons}
                                                    </div>
                                                );
                                            },
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Datasheet Language for version naming */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language === "zh"
                                    ? "資料表語言"
                                    : "Datasheet Language"}
                            </label>
                            {isEditMode ? (
                                // An edit stays in its version's language — the new
                                // version is the next one in that same sequence.
                                <div className="w-full md:w-1/3 border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-600">
                                    {(
                                        availableLangCodes.find(
                                            (c) => c.id === selectedLangCode,
                                        )?.name || selectedLangCode
                                    ).toString()}
                                    <span className="ml-2 text-xs text-gray-400">
                                        ({editVersionLabel})
                                    </span>
                                </div>
                            ) : (
                            <select
                                value={selectedLangCode}
                                onChange={(e) => {
                                    setSelectedLangCode(e.target.value);
                                    if (validationErrors.langCode)
                                        setValidationErrors((prev) => {
                                            const { langCode, ...rest } = prev;
                                            return rest;
                                        });
                                }}
                                className={`w-full md:w-1/3 border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 ${validationErrors.langCode ? "border-red-500 ring-2 ring-red-200" : "border-gray-300"}`}
                            >
                                <option value="">
                                    {availableLangCodes.length === 0
                                        ? language === "zh"
                                            ? "載入中..."
                                            : "Loading..."
                                        : language === "zh"
                                          ? "請選擇語言"
                                          : "Select language"}
                                </option>
                                {availableLangCodes.map((code) => (
                                    <option key={code.id} value={code.id}>
                                        {code.id} - {code.name}
                                    </option>
                                ))}
                            </select>
                            )}
                            {validationErrors.langCode && (
                                <p className="text-red-500 text-xs mt-1">
                                    {validationErrors.langCode}
                                </p>
                            )}
                        </div>

                        {/* Action buttons */}
                        <div className="flex justify-center mt-8 space-x-4">
                            <button
                                className="px-8 py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={
                                    isEditMode
                                        ? handleUpdateVersion
                                        : handleSaveData
                                }
                                disabled={
                                    loading ||
                                    (!isEditMode && productData.length === 0)
                                }
                            >
                                {isEditMode
                                    ? language === "zh"
                                        ? "儲存為新版本"
                                        : "Save as New Version"
                                    : language === "zh"
                                      ? "保存數據"
                                      : "Save Data"}
                            </button>
                            <button
                                className="px-8 py-3 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
                                onClick={handleGoBack}
                            >
                                {isEditMode
                                    ? language === "zh"
                                        ? "取消"
                                        : "Cancel"
                                    : language === "zh"
                                      ? "返回上傳頁面"
                                      : "Back to Upload"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Duplicate Product Modal ──────────────────────────────── */}
            {duplicateModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                        {/* Header */}
                        <div className="bg-orange-500 border-b border-amber-200 px-6 py-4 flex items-center gap-3">
                            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                                <svg
                                    className="w-5 h-5 text-amber-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-amber-900">
                                {language === "zh"
                                    ? "重複產品"
                                    : "Duplicate Product Found"}
                            </h3>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5 space-y-4">
                            <p className="text-gray-700 text-sm leading-relaxed">
                                {language === "zh"
                                    ? `名稱為「${duplicateModal.existingProduct?.model_id || productName}」的產品已存在。請選擇操作方式：`
                                    : `A product with the name "${duplicateModal.existingProduct?.model_id || productName}" already exists. Choose an action:`}
                            </p>
                            {/* Existing product info card */}
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">
                                        {language === "zh"
                                            ? "產品 ID"
                                            : "Product ID"}
                                    </span>
                                    <span className="font-mono text-gray-800">
                                        {
                                            duplicateModal.existingProduct
                                                ?.product_id
                                        }
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">
                                        {language === "zh" ? "型號" : "Model"}
                                    </span>
                                    <span className="font-medium text-gray-800">
                                        {
                                            duplicateModal.existingProduct
                                                ?.model_id
                                        }
                                    </span>
                                </div>
                                {duplicateModal.existingProduct
                                    ?.current_version_label && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">
                                            {language === "zh"
                                                ? "目前版本"
                                                : "Current Version"}
                                        </span>
                                        <span className="text-gray-800">
                                            {
                                                duplicateModal.existingProduct
                                                    .current_version_label
                                            }
                                        </span>
                                    </div>
                                )}
                            </div>
                            {/* Per-language version summary */}
                            {duplicateModal.versionSummary &&
                                Object.keys(duplicateModal.versionSummary)
                                    .length > 0 && (
                                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-sm">
                                        <p className="font-medium text-blue-800 mb-2">
                                            {language === "zh"
                                                ? "各語言版本數量"
                                                : "Versions by Language"}
                                        </p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {Object.entries(
                                                duplicateModal.versionSummary,
                                            )
                                                .sort(([a], [b]) =>
                                                    a.localeCompare(b),
                                                )
                                                .map(([lang, count]) => (
                                                    <div
                                                        key={lang}
                                                        className="flex justify-between px-2 py-1 bg-white rounded border border-blue-100"
                                                    >
                                                        <span className="text-gray-600 uppercase font-mono">
                                                            {lang}
                                                        </span>
                                                        <span className="font-medium text-blue-700">
                                                            {count}
                                                        </span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}{" "}
                            {/* Language code dropdown for version naming */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    {language === "zh"
                                        ? "資料表語言"
                                        : "Datasheet Language"}
                                </label>
                                <select
                                    value={selectedLangCode}
                                    onChange={(e) =>
                                        setSelectedLangCode(e.target.value)
                                    }
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                                >
                                    <option value="">
                                        {availableLangCodes.length === 0
                                            ? language === "zh"
                                                ? "載入中..."
                                                : "Loading..."
                                            : language === "zh"
                                              ? "請選擇語言"
                                              : "Select language"}
                                    </option>
                                    {availableLangCodes.map((code) => (
                                        <option key={code.id} value={code.id}>
                                            {code.id} - {code.name}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500">
                                    {language === "zh"
                                        ? "版本標籤將由後端自動產生。"
                                        : "The version label will be auto-generated by the server."}
                                </p>
                                {selectedLangCode &&
                                    selectedLangCode !== "en" && (
                                        <p className="text-xs text-blue-500 mt-1">
                                            {language === "zh"
                                                ? "非英文版本僅儲存至版本紀錄，不會更新主產品資料。"
                                                : "Non-English versions are stored as version records only — the main product data will not be updated."}
                                        </p>
                                    )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="px-6 pb-6 flex flex-col gap-2.5">
                            <button
                                onClick={handleUpdateExisting}
                                disabled={loading}
                                className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {language === "zh"
                                    ? "更新現有產品（新版本）"
                                    : "Update Existing (New Version)"}
                            </button>
                            <button
                                onClick={handleCancelDuplicate}
                                disabled={loading}
                                className="w-full px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 border border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {language === "zh" ? "取消" : "Cancel"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductProperties;
